import { useEffect, useState } from "react";
import { t } from "i18next";
import { Input } from "@material-tailwind/react";
import TechnicianSelect from "../../../components/data/technician/TechnicianSelect";
import RangeDatePicker from "../../../components/data/date/RangeDatePicker";
import Switch from "../../../components/switch/Switch";
import { createDateQuery, createDateRangeQuery, createListQuery, createSingleQuery, createTextSearchQuery, SearchQuery } from "../../../services/api/search";

interface ProspectsCustomerFilterFormProps {
  data?: Partial<EasyTech.ProspectsFilterType>;
  onChange: (name: string, value: any) => void;
  onSubmit: () => void;
  submitted?: boolean;
}

export const getQueriesFromFilter = (filters: Partial<EasyTech.ProspectsFilterType>): SearchQuery[] => {
  const queries: SearchQuery[] = [];

  // Recherche par référence
  if (filters.ref) {
    queries.push(createTextSearchQuery(["ref"], filters.ref));
  }

  // Filtre par technicien
  if (filters.technician) {
    if (Array.isArray(filters.technician)) {
      const techIds = filters.technician.map((t: any) => typeof t === "string" ? t : t.id);
      if (techIds.length === 1) {
        queries.push(createSingleQuery("technician.id", techIds[0]));
      } else if (techIds.length > 1) {
        queries.push(createListQuery("technician.id", techIds));
      }
    } else if (typeof filters.technician === "object" && (filters.technician as any).id) {
      queries.push(createSingleQuery("technician.id", (filters.technician as any).id));
    } else if (typeof filters.technician === "string") {
      queries.push(createSingleQuery("technician.id", filters.technician));
    }
  }

  // Filtre par date et par heure (spécificité des Prospects/Visites)
  // Dans la base de données, pour l'entité Prospect (Visite), le champ 'date' est souvent stocké
  // à minuit pile (00:00:00). L'heure réelle est stockée séparément sous forme de chaîne de caractères
  // dans le champ 'hour' (ex: "14:30").
  // Pour pouvoir correctement filtrer les visites sur une plage horaire le même jour :
  // 1. La requête backend sur le champ 'date' couvre systématiquement la journée complète (de 00:00:00 à 23:59:59).
  // 2. Des requêtes conditionnelles additionnelles basées sur le champ 'hour' (ex: > "06:59" et < "19:01") 
  //    sont générées pour affiner et respecter les heures choisies dans l'UI.
  if (filters.startDate || filters.endDate) {
    if (filters.startDate && filters.endDate) {
      const startDate = new Date(filters.startDate);
      const endDate = new Date(filters.endDate);
      const queryStartDate = new Date(Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth(), startDate.getUTCDate(), 0, 0, 0, 0));
      const queryEndDate = new Date(Date.UTC(endDate.getUTCFullYear(), endDate.getUTCMonth(), endDate.getUTCDate(), 23, 59, 59, 999));
      queries.push(createDateRangeQuery("date", queryStartDate, queryEndDate, "BETWEEN"));
      
      const sHH = startDate.getUTCHours();
      const sMM = startDate.getUTCMinutes();
      if (sHH !== 0 || sMM !== 0) {
          const adjustedStart = new Date(startDate.getTime() - 60000); // -1 min for inclusive >= 
          const startHourStr = `${String(adjustedStart.getUTCHours()).padStart(2, '0')}:${String(adjustedStart.getUTCMinutes()).padStart(2, '0')}`;
          queries.push(createSingleQuery("hour", startHourStr, "GREATER_THAN"));
      }
      
      const eHH = endDate.getUTCHours();
      const eMM = endDate.getUTCMinutes();
      if (eHH !== 23 || eMM !== 59) {
          const adjustedEnd = new Date(endDate.getTime() + 60000); // +1 min for inclusive <=
          const endHourStr = `${String(adjustedEnd.getUTCHours()).padStart(2, '0')}:${String(adjustedEnd.getUTCMinutes()).padStart(2, '0')}`;
          queries.push(createSingleQuery("hour", endHourStr, "LESS_THAN"));
      }

    } else if (filters.startDate) {
      const startDate = new Date(filters.startDate);
      const queryStartDate = new Date(Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth(), startDate.getUTCDate(), 0, 0, 0, 0));
      queries.push(createDateQuery("date", queryStartDate, "GREATER_THAN"));
      
      const sHH = startDate.getUTCHours();
      const sMM = startDate.getUTCMinutes();
      if (sHH !== 0 || sMM !== 0) {
          const adjustedStart = new Date(startDate.getTime() - 60000);
          const startHourStr = `${String(adjustedStart.getUTCHours()).padStart(2, '0')}:${String(adjustedStart.getUTCMinutes()).padStart(2, '0')}`;
          queries.push(createSingleQuery("hour", startHourStr, "GREATER_THAN"));
      }

    } else if (filters.endDate) {
      const endDate = new Date(filters.endDate);
      const queryEndDate = new Date(Date.UTC(endDate.getUTCFullYear(), endDate.getUTCMonth(), endDate.getUTCDate(), 23, 59, 59, 999));
      queries.push(createDateQuery("date", queryEndDate, "LESS_THAN"));
      
      const eHH = endDate.getUTCHours();
      const eMM = endDate.getUTCMinutes();
      if (eHH !== 23 || eMM !== 59) {
          const adjustedEnd = new Date(endDate.getTime() + 60000);
          const endHourStr = `${String(adjustedEnd.getUTCHours()).padStart(2, '0')}:${String(adjustedEnd.getUTCMinutes()).padStart(2, '0')}`;
          queries.push(createSingleQuery("hour", endHourStr, "LESS_THAN"));
      }
    }
  }

  if (filters.disabled)
    queries.push(createSingleQuery("enabled", false));
  return queries;
};


const ProspectsCustomerFilterForm = (props: ProspectsCustomerFilterFormProps) => {
  const [formData, setFormData] = useState<Partial<EasyTech.ProspectsFilterType>>({
    ref: "",
    startDate: undefined,
    endDate: undefined,
    technician: undefined,
    customer: undefined,
    disabled: false
  });

  useEffect(() => {
    if (props.data) {
      setFormData({
        ...props.data,
        startDate: props.data.startDate ? new Date(props.data.startDate as any) : undefined,
        endDate: props.data.endDate ? new Date(props.data.endDate as any) : undefined,
      });
    } else {
      setFormData({
        ref: "",
        startDate: undefined,
        endDate: undefined,
        technician: undefined,
        customer: undefined,
        disabled: false
      });
    }
  }, [props.data]);

  const handleChange = (value: any, name: string) => {
    const tmp = structuredClone(formData);
    if (["customer", "technician"].includes(name)) {
      tmp[name as keyof Partial<EasyTech.ProspectsFilterType>] = Array.isArray(value) ? value[0] : value;
    } else {
      tmp[name as keyof Partial<EasyTech.ProspectsFilterType>] = value;
    }
    props.onChange(name, tmp[name as keyof Partial<EasyTech.ProspectsFilterType>]);
    setFormData(tmp);
  };

  const handleDateRangeChange = (update: [Date | null, Date | null]) => {
    const [start, end] = update;
    const tmp = structuredClone(formData);
    tmp.startDate = start || undefined;
    tmp.endDate = end || undefined;
    props.onChange("startDate", tmp.startDate);
    props.onChange("endDate", tmp.endDate);
    setFormData(tmp);
  };

  const submit = (e: any) => {
    e.preventDefault();
    if (formData.startDate && !formData.endDate) return;
    if (formData.endDate && !formData.startDate) return;
    props.onSubmit();
  };

  return (
    <form onSubmit={submit} className="grid gap-4 p-2" key={"edit-prospects-filter"}>
      <div className="grid grid-cols-1 gap-4">
        <div className="field">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("common:form:fields:ref:label")}</label>
          <Input
            value={formData.ref || ""}
            onChange={(e) => handleChange(e.target.value, "ref")}
            className="w-full"
            size="md"
            placeholder={t("common:ref:placeholder")}
          />
        </div>

        <div className="field">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("workorder:filters.creationDate.label")}</label>
          <RangeDatePicker
            startDate={formData.startDate as any}
            endDate={formData.endDate as any}
            onChange={handleDateRangeChange}
            withTime={true}
            placeholderText={t("common:date:placeholder")}
          />
        </div>

        <div className="field">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("prospect:form:fields:technician:label")}</label>
          <TechnicianSelect
            onChange={(technicians) => handleChange(technicians, "technician")}
            value={formData.technician as any}
            clearable={true}
            isMulti={false}
          />
        </div>

        <div className="field">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("workorder:filters:disabled:label")}</label>
          <Switch checked={formData.disabled}
            onChange={(b) => handleChange(b, "disabled")} />
        </div>
      </div>
    </form>
  );
};

export default ProspectsCustomerFilterForm; 